// Página CRUD de FAQ (migration 025).
//
// Solo super_admin. Lista las FAQs con orden, categoría, pregunta y
// respuesta. Edición inline en modal. Reorder con flechas arriba/abajo
// (en vez de drag-drop que requiere lib extra). El orden se persiste
// como múltiplo de 10 para dejar espacio entre items al reinsertar.

import { useCallback, useEffect, useState } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  Loader2,
  ChevronUp,
  ChevronDown,
  AlertCircle,
} from "lucide-react";
import {
  adminListFaqs,
  superCreateFaq,
  superUpdateFaq,
  superDeleteFaq,
  superReorderFaqs,
  type FaqRow,
} from "../../lib/api";
import { Modal } from "../../components/ui/Modal";
import { HelpPopover } from "../../components/admin/forms/HelpPopover";
import { useAdminCtx } from "./AdminLayout";
import { useDialogs } from "../../hooks/useDialogs";
import { useUndoableDelete } from "../../hooks/useUndoableDelete";

const CATEGORIAS_CONOCIDAS = [
  "general",
  "productos",
  "juegos",
  "sucursales",
  "super_admin",
] as const;

const CATEGORIA_LABELS: Record<string, string> = {
  general: "General",
  productos: "Productos",
  juegos: "Juegos",
  sucursales: "Sucursales",
  super_admin: "Super admin",
};

export default function Faqs() {
  const { user } = useAdminCtx();
  const dialogs = useDialogs();
  const undoDelete = useUndoableDelete();
  const [faqs, setFaqs] = useState<FaqRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<FaqRow | "new" | null>(null);
  const [reordering, setReordering] = useState(false);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await adminListFaqs();
      setFaqs(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  function handleDelete(f: FaqRow) {
    undoDelete({
      key: f.id,
      message: `Pregunta "${f.pregunta}" borrada`,
      removeOptimistic: () =>
        setFaqs((prev) => prev.filter((x) => x.id !== f.id)),
      restore: () =>
        setFaqs((prev) =>
          prev.some((x) => x.id === f.id) ? prev : [...prev, f],
        ),
      commit: () => superDeleteFaq(f.id),
      onError: (e) => {
        dialogs.alert({
          message: e instanceof Error ? e.message : String(e),
          variant: "error",
        });
        refetch();
      },
    });
  }

  // Mover una FAQ una posición arriba/abajo. Reconstruye el array
  // local y manda el reorder al backend. Optimista (UI se actualiza
  // antes de confirmar) para que la flecha se sienta responsiva.
  async function move(idx: number, dir: -1 | 1) {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= faqs.length) return;
    const next = [...faqs];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    setFaqs(next);
    setReordering(true);
    try {
      await superReorderFaqs(next.map((f) => f.id));
    } catch (e) {
      await dialogs.alert({
        message: e instanceof Error ? e.message : String(e),
        variant: "error",
      });
      refetch(); // rollback
    } finally {
      setReordering(false);
    }
  }

  // Solo super_admin. El AdminLayout ya redirige a /admin antes de que
  // este componente monte (ver useEffect con SUPER_ONLY_PATHS). Este
  // guard es defensa adicional: si algo cambia y se llega acá, mostramos
  // un mensaje neutro (sin mencionar roles internos al cliente).
  if (user.role !== "super_admin") {
    return (
      <div className="rounded-lg bg-red-500/15 p-6 text-red-300">
        <AlertCircle className="mb-2 h-6 w-6" />
        No tenés permisos para ver esta página.
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-end gap-4">
        <h1 className="sr-only">
          Preguntas frecuentes
          <HelpPopover>
            {"Estas FAQs las ven todos los admins en el modal de ayuda " +
              "(botón ?). Los cambios son instantáneos, sin deploy."}
          </HelpPopover>
        </h1>
        <button
          type="button"
          onClick={() => setEditing("new")}
          data-tour="faqs-new"
          className="flex shrink-0 items-center gap-2 rounded-lg bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] px-4 py-2 font-bold transition hover:scale-[1.02]"
        >
          <Plus className="h-4 w-4" />
          Nueva pregunta
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-500/15 p-4 text-sm text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex h-48 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin opacity-50" />
        </div>
      ) : faqs.length === 0 ? (
        <div className="rounded-lg bg-white/5 px-4 py-8 text-center opacity-70">
          No hay preguntas cargadas. Creá la primera con el botón de arriba.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
          {faqs.map((f, idx) => (
            <FaqCard
              key={f.id}
              entry={f}
              idx={idx}
              total={faqs.length}
              reordering={reordering}
              onEdit={() => setEditing(f)}
              onDelete={() => handleDelete(f)}
              onMove={(dir) => move(idx, dir)}
            />
          ))}
        </div>
      )}

      {editing !== null && (
        <Modal
          open
          onClose={() => setEditing(null)}
          closeOnBackdrop={false}
        >
          <FaqForm
            entry={editing === "new" ? null : editing}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              refetch();
            }}
          />
        </Modal>
      )}
    </div>
  );
}

function FaqCard({
  entry,
  idx,
  total,
  reordering,
  onEdit,
  onDelete,
  onMove,
}: {
  entry: FaqRow;
  idx: number;
  total: number;
  reordering: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onMove: (dir: -1 | 1) => void;
}) {
  const catLabel = CATEGORIA_LABELS[entry.categoria] ?? entry.categoria;
  return (
    <div className="flex h-full items-start gap-2 rounded-lg bg-white/5 p-3">
      {/* Reorder flechas (UI: arriba/abajo). Drag-drop sería más
          natural pero requiere otra lib; con pocas FAQs (~10-20) las
          flechas alcanzan. */}
      <div className="flex flex-col gap-1 pt-1">
        <button
          type="button"
          disabled={idx === 0 || reordering}
          onClick={() => onMove(-1)}
          className="rounded p-1 opacity-50 hover:bg-white/10 hover:opacity-100 disabled:opacity-20"
          aria-label="Subir"
        >
          <ChevronUp className="h-3 w-3" />
        </button>
        <button
          type="button"
          disabled={idx === total - 1 || reordering}
          onClick={() => onMove(1)}
          className="rounded p-1 opacity-50 hover:bg-white/10 hover:opacity-100 disabled:opacity-20"
          aria-label="Bajar"
        >
          <ChevronDown className="h-3 w-3" />
        </button>
      </div>

      <div className="flex-1 min-w-0">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <span className="rounded bg-[#a78bfa]/20 px-2 py-0.5 text-xs text-[#c4b5fd]">
            {catLabel}
          </span>
          <span className="text-xs opacity-40">orden: {entry.orden}</span>
        </div>
        <h3 className="text-sm font-semibold">{entry.pregunta}</h3>
        <p className="mt-1 whitespace-pre-wrap text-sm opacity-70">
          {entry.respuesta}
        </p>
      </div>

      <div className="flex shrink-0 gap-1">
        <button
          type="button"
          onClick={onEdit}
          className="rounded p-2 opacity-70 hover:bg-white/10 hover:opacity-100"
          aria-label="Editar"
        >
          <Edit2 className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="rounded p-2 text-red-400 hover:bg-red-500/15"
          aria-label="Borrar"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function FaqForm({
  entry,
  onCancel,
  onSaved,
}: {
  entry: FaqRow | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const isEdit = entry !== null;
  const [pregunta, setPregunta] = useState(entry?.pregunta ?? "");
  const [respuesta, setRespuesta] = useState(entry?.respuesta ?? "");
  const [categoria, setCategoria] = useState(entry?.categoria ?? "general");
  const [categoriaCustom, setCategoriaCustom] = useState("");
  const [usandoCustom, setUsandoCustom] = useState(
    !!entry && !(CATEGORIAS_CONOCIDAS as readonly string[]).includes(entry.categoria),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Si abrimos con categoría custom (no en el catálogo), arrancamos
  // con el input custom prellenado.
  useEffect(() => {
    if (entry && !(CATEGORIAS_CONOCIDAS as readonly string[]).includes(entry.categoria)) {
      setCategoriaCustom(entry.categoria);
    }
  }, [entry]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const p = pregunta.trim();
    const r = respuesta.trim();
    const cat = usandoCustom ? categoriaCustom.trim().toLowerCase() : categoria;
    if (p.length === 0 || r.length === 0) {
      setError("Pregunta y respuesta son obligatorias.");
      return;
    }
    if (cat.length === 0) {
      setError("Categoría no puede estar vacía.");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await superUpdateFaq(entry.id, {
          pregunta: p,
          respuesta: r,
          categoria: cat,
        });
      } else {
        await superCreateFaq({ pregunta: p, respuesta: r, categoria: cat });
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <h2 className="text-2xl font-bold">
        {isEdit ? "Editar pregunta" : "Nueva pregunta"}
      </h2>

      <label className="flex flex-col gap-1 text-sm">
        <span className="opacity-80">Pregunta</span>
        <input
          value={pregunta}
          onChange={(e) => setPregunta(e.target.value)}
          placeholder="¿Cómo agrego un nuevo premio?"
          required
          className="rounded-lg bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#a78bfa]"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="opacity-80">Respuesta</span>
        <textarea
          value={respuesta}
          onChange={(e) => setRespuesta(e.target.value)}
          placeholder="Andá a Productos → 'Nuevo producto'…"
          required
          rows={5}
          className="resize-y rounded-lg bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#a78bfa]"
        />
        <span className="text-xs opacity-50">
          Saltos de línea se respetan. No hay markdown todavía.
        </span>
      </label>

      <div className="flex flex-col gap-1 text-sm">
        <span className="opacity-80">Categoría</span>
        <div className="flex gap-2">
          <select
            value={usandoCustom ? "__custom" : categoria}
            onChange={(e) => {
              if (e.target.value === "__custom") {
                setUsandoCustom(true);
              } else {
                setUsandoCustom(false);
                setCategoria(e.target.value);
              }
            }}
            className="flex-1 rounded-lg bg-white/10 px-3 py-2 outline-none"
          >
            {CATEGORIAS_CONOCIDAS.map((c) => (
              <option key={c} value={c}>
                {CATEGORIA_LABELS[c]}
              </option>
            ))}
            <option value="__custom">Otra (custom)…</option>
          </select>
          {usandoCustom && (
            <input
              value={categoriaCustom}
              onChange={(e) => setCategoriaCustom(e.target.value)}
              placeholder="ej: mantenimiento"
              className="flex-1 rounded-lg bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#a78bfa]"
            />
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 rounded-lg bg-white/10 py-3 font-bold hover:bg-white/20"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex-1 rounded-lg bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] py-3 font-bold disabled:opacity-50"
        >
          {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear"}
        </button>
      </div>
    </form>
  );
}
